/**
 * Widget registry and layout resolution for the customizable home dashboards
 * (flag `customizable_dashboards`). Pure: no React, no data fetching.
 *
 * A layout holds widget ids, order, size and config only. The ids, sizes and
 * gates here mirror the server catalog (`src/types/dashboardLayout.ts`), which
 * is what actually drops a flag-off or plan-locked widget from a saved layout;
 * the client filters too so a stale response can never render one.
 *
 * `DEFAULT_*_LAYOUT` reproduces today's fixed pages exactly, in their order.
 * Until someone saves a layout (or an owner sets an org default) the pages
 * keep rendering their original fixed markup.
 */
import type { OrgFeatureFlagKey } from '../../utils/orgFeatureFlagKeys';
import { KPI_CATALOG, KPI_MAX } from './kpiCatalog';

export { KPI_MAX };

export type DashboardShell = 'store' | 'vendor';
export type WidgetSize = 'narrow' | 'wide' | 'full';
export type WidgetWindow = '4w' | '12w' | '26w';

export interface WidgetConfig {
  brand?: string;
  productType?: string;
  locationId?: string;
  window?: WidgetWindow;
  /** Key numbers tile: ordered metric ids from `kpiCatalog.ts`. Unset means the shell's default list. */
  kpis?: string[];
}

export interface LayoutWidget {
  id: string;
  order: number;
  size: WidgetSize;
  config: WidgetConfig;
}

export interface DashboardLayout {
  version: 1;
  widgets: LayoutWidget[];
}

export type WidgetAvailability = 'available' | 'locked';

export interface WidgetDefinition {
  id: string;
  /** Key under `dashboardLayout.widgets.<id>` in the locale files. */
  labelKey: string;
  defaultSize: WidgetSize;
  allowedSizes: readonly WidgetSize[];
  configKeys: readonly (keyof WidgetConfig)[];
  /** Part of today's page: in the built-in default layout. */
  inDefault: boolean;
  requiredFlag?: OrgFeatureFlagKey;
  /** `PlanLimits` key the plan must grant; the server reports the result as `availability`. */
  requiredLimit?: string;
}

const STORE_SIZES = ['narrow', 'wide', 'full'] as const;
const VENDOR_SIZES = ['narrow', 'full'] as const;

/** Declaration order is the built-in `defaultOrder`. */
export const STORE_WIDGETS: readonly WidgetDefinition[] = [
  { id: 'kpis', labelKey: 'kpis', defaultSize: 'full', allowedSizes: ['full'], configKeys: ['kpis'], inDefault: true },
  { id: 'sync_chart', labelKey: 'syncChart', defaultSize: 'wide', allowedSizes: STORE_SIZES, configKeys: ['window'], inDefault: true },
  { id: 'attention', labelKey: 'attention', defaultSize: 'narrow', allowedSizes: STORE_SIZES, configKeys: [], inDefault: true },
  { id: 'activity', labelKey: 'activity', defaultSize: 'wide', allowedSizes: STORE_SIZES, configKeys: [], inDefault: true },
  { id: 'jump_to', labelKey: 'jumpTo', defaultSize: 'narrow', allowedSizes: STORE_SIZES, configKeys: [], inDefault: true },
  { id: 'rebates_identified', labelKey: 'rebatesIdentified', defaultSize: 'narrow', allowedSizes: STORE_SIZES, configKeys: [], inDefault: false, requiredLimit: 'rebateTracking' },
  { id: 'reorder', labelKey: 'reorder', defaultSize: 'narrow', allowedSizes: STORE_SIZES, configKeys: [], inDefault: false, requiredFlag: 'predictive_reorder', requiredLimit: 'advancedAnalytics' },
];

export const VENDOR_WIDGETS: readonly WidgetDefinition[] = [
  { id: 'kpis', labelKey: 'kpis', defaultSize: 'full', allowedSizes: ['full'], configKeys: ['kpis'], inDefault: true },
  { id: 'sell_through_sharing', labelKey: 'sellThroughSharing', defaultSize: 'full', allowedSizes: VENDOR_SIZES, configKeys: [], inDefault: true, requiredFlag: 'sell_through_sharing' },
  { id: 'attention', labelKey: 'attention', defaultSize: 'full', allowedSizes: ['full'], configKeys: [], inDefault: true },
  { id: 'stores', labelKey: 'stores', defaultSize: 'narrow', allowedSizes: VENDOR_SIZES, configKeys: [], inDefault: true },
  { id: 'ingestion_feed', labelKey: 'ingestionFeed', defaultSize: 'narrow', allowedSizes: VENDOR_SIZES, configKeys: [], inDefault: true },
  { id: 'rebate_files', labelKey: 'rebateFiles', defaultSize: 'narrow', allowedSizes: VENDOR_SIZES, configKeys: [], inDefault: true },
  { id: 'sync_history', labelKey: 'syncHistory', defaultSize: 'narrow', allowedSizes: VENDOR_SIZES, configKeys: [], inDefault: true },
  { id: 'managed_brands', labelKey: 'managedBrands', defaultSize: 'narrow', allowedSizes: VENDOR_SIZES, configKeys: [], inDefault: true },
  { id: 'activity', labelKey: 'activity', defaultSize: 'narrow', allowedSizes: VENDOR_SIZES, configKeys: [], inDefault: true },
];

export const WIDGET_REGISTRY: Record<DashboardShell, readonly WidgetDefinition[]> = {
  store: STORE_WIDGETS,
  vendor: VENDOR_WIDGETS,
};

/**
 * Clean a saved Key numbers list: unknown ids dropped (against the shell's catalog),
 * repeats dropped (first wins), at most `KPI_MAX`. Undefined when nothing is left,
 * which reads as "use the default list". A plan-locked id stays: it is skipped when
 * drawing, never deleted from what the user saved.
 */
export function sanitizeKpiOrder(shell: DashboardShell, ids: readonly unknown[] | undefined): string[] | undefined {
  if (!Array.isArray(ids)) return undefined;
  const known = new Set(KPI_CATALOG[shell].map((k) => k.id));
  const out: string[] = [];
  for (const id of ids) {
    if (typeof id !== 'string' || !known.has(id) || out.includes(id)) continue;
    out.push(id);
    if (out.length >= KPI_MAX) break;
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Catalog numbers the plan does not include (never listed, never drawn, never deleted
 * from a saved list). A limit counts as granted when a widget that requires it is
 * `available` in the server's availability map, the same signal the widget gate uses.
 */
export function lockedKpiIds(shell: DashboardShell, availability: Record<string, WidgetAvailability> | undefined): string[] {
  const granted = new Set(
    WIDGET_REGISTRY[shell].filter((w) => w.requiredLimit && availability?.[w.id] === 'available').map((w) => w.requiredLimit)
  );
  return KPI_CATALOG[shell].filter((k) => k.requiredLimit && !granted.has(k.requiredLimit)).map((k) => k.id);
}

export const widgetDefinition = (shell: DashboardShell, id: string): WidgetDefinition | undefined =>
  WIDGET_REGISTRY[shell].find((w) => w.id === id);

export const buildDefaultLayout = (shell: DashboardShell): DashboardLayout => ({
  version: 1,
  widgets: WIDGET_REGISTRY[shell]
    .filter((w) => w.inDefault)
    .map((w, order) => ({ id: w.id, order, size: w.defaultSize, config: {} })),
});

export const DEFAULT_STORE_LAYOUT = buildDefaultLayout('store');
export const DEFAULT_VENDOR_LAYOUT = buildDefaultLayout('vendor');

export interface ServerDashboardLayout {
  source: 'user' | 'organization' | 'default';
  layout: DashboardLayout | null;
  availability: Record<string, WidgetAvailability>;
}

export interface ResolveContext {
  /** From `GET /session/dashboard-layout`; missing means the built-in default. */
  server?: ServerDashboardLayout | null;
  isFlagEnabled: (key: OrgFeatureFlagKey) => boolean;
  /**
   * When `show_locked_plan_features` is on, a widget listed in `planLockedIds`
   * stays in the layout so the page can render it greyed out. Any other
   * plan-locked widget is still dropped, including while the plan read is
   * pending or has failed. A rollout flag that is off still drops the widget.
   */
  includePlanLocked?: boolean;
  /** Widget ids whose plan surface has resolved to locked. */
  planLockedIds?: readonly string[];
  /** Where a flag's value came from. A `plan` source that is off is a plan lock. */
  flagSource?: (key: OrgFeatureFlagKey) => 'default' | 'global' | 'organization' | 'plan' | undefined;
}

/**
 * The widgets to render, in order: the saved layout (user, then org, as the
 * server resolved it) or the built-in default, minus unknown ids, flag-off
 * widgets, and any widget the server reports as locked by plan.
 */
export function resolveDashboardLayout(shell: DashboardShell, ctx: ResolveContext): LayoutWidget[] {
  const base = ctx.server?.layout ?? buildDefaultLayout(shell);
  const availability = ctx.server?.availability;
  const seen = new Set<string>();
  return [...base.widgets]
    .sort((a, b) => a.order - b.order)
    .filter((w) => {
      const def = widgetDefinition(shell, w.id);
      if (!def || seen.has(w.id)) return false;
      const showLocked = Boolean(ctx.includePlanLocked && ctx.planLockedIds?.includes(w.id));
      if (def.requiredFlag && !ctx.isFlagEnabled(def.requiredFlag)) {
        const lockedByPlan = showLocked && ctx.flagSource?.(def.requiredFlag) === 'plan';
        if (!lockedByPlan) return false;
      }
      if (!showLocked) {
        if (availability && availability[w.id] === 'locked') return false;
        // A plan-gated widget is only rendered once the server says it is available.
        if (def.requiredLimit && availability?.[w.id] !== 'available') return false;
      }
      seen.add(w.id);
      return true;
    })
    .map((w, order) => {
      const def = widgetDefinition(shell, w.id)!;
      const config = Object.fromEntries(
        Object.entries(w.config ?? {}).filter(([k]) => (def.configKeys as readonly string[]).includes(k))
      ) as WidgetConfig;
      if ('kpis' in config) {
        const kpis = sanitizeKpiOrder(shell, config.kpis);
        if (kpis) config.kpis = kpis;
        else delete config.kpis;
      }
      return {
        id: w.id,
        order,
        size: def.allowedSizes.includes(w.size) ? w.size : def.defaultSize,
        config,
      };
    });
}

/** True when the page should keep its original fixed markup. */
export const usesFixedLayout = (server: ServerDashboardLayout | null | undefined): boolean =>
  !server || server.source === 'default';

export interface AddableWidget {
  def: WidgetDefinition;
  locked: boolean;
}

/** What the "Add widget" menu lists: absent widgets that are not flag-off. */
export function addableWidgets(
  shell: DashboardShell,
  current: LayoutWidget[],
  ctx: ResolveContext
): AddableWidget[] {
  const present = new Set(current.map((w) => w.id));
  return WIDGET_REGISTRY[shell]
    .filter((def) => !present.has(def.id))
    .filter((def) => !def.requiredFlag || ctx.isFlagEnabled(def.requiredFlag))
    .map((def) => ({
      def,
      locked: !!def.requiredLimit && ctx.server?.availability?.[def.id] !== 'available',
    }));
}

const renumber = (widgets: LayoutWidget[]): LayoutWidget[] => widgets.map((w, order) => ({ ...w, order }));

export function addWidget(shell: DashboardShell, widgets: LayoutWidget[], id: string): LayoutWidget[] {
  const def = widgetDefinition(shell, id);
  if (!def || widgets.some((w) => w.id === id)) return widgets;
  return renumber([...widgets, { id, order: widgets.length, size: def.defaultSize, config: {} }]);
}

export const removeWidget = (widgets: LayoutWidget[], id: string): LayoutWidget[] =>
  renumber(widgets.filter((w) => w.id !== id));

/**
 * Swap a widget with its neighbour. With `visibleIds` (the widgets that actually
 * render) the neighbour is the next visible one, so a widget with nothing to show
 * is never swapped with; it keeps its place in the saved list.
 */
export function moveWidget(widgets: LayoutWidget[], id: string, delta: -1 | 1, visibleIds?: readonly string[]): LayoutWidget[] {
  if (visibleIds) {
    const order = widgets.filter((w) => visibleIds.includes(w.id)).map((w) => w.id);
    const at = order.indexOf(id) + delta;
    if (order.indexOf(id) < 0 || at < 0 || at >= order.length) return widgets;
    const a = widgets.findIndex((w) => w.id === id);
    const b = widgets.findIndex((w) => w.id === order[at]);
    const next = [...widgets];
    [next[a], next[b]] = [next[b], next[a]];
    return renumber(next);
  }
  const from = widgets.findIndex((w) => w.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= widgets.length) return widgets;
  const next = [...widgets];
  [next[from], next[to]] = [next[to], next[from]];
  return renumber(next);
}

/**
 * Move a widget to `toIndex` in the list (the slot it should end up in, as a
 * sortable list reports it). Out-of-range indexes clamp; an unknown id is a no-op.
 * With `visibleIds` (the widgets that actually render) `toIndex` is a position among
 * those: only the visible widgets are reordered, and a widget with nothing to show
 * keeps its place in the saved list.
 */
export function moveWidgetTo(widgets: LayoutWidget[], id: string, toIndex: number, visibleIds?: readonly string[]): LayoutWidget[] {
  if (visibleIds) {
    const order = widgets.filter((w) => visibleIds.includes(w.id));
    const from = order.findIndex((w) => w.id === id);
    if (from < 0) return widgets;
    const to = Math.max(0, Math.min(order.length - 1, toIndex));
    if (to === from) return widgets;
    const reordered = [...order];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    // Refill the slots the visible widgets occupied; hidden ones stay where they are.
    let next = 0;
    return renumber(widgets.map((w) => (visibleIds.includes(w.id) ? reordered[next++] : w)));
  }
  const from = widgets.findIndex((w) => w.id === id);
  if (from < 0) return widgets;
  const to = Math.max(0, Math.min(widgets.length - 1, toIndex));
  if (to === from) return widgets;
  const next = [...widgets];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return renumber(next);
}

/** Add an absent widget at `index` (clamped) at its default size; present or unknown ids are a no-op. */
export function insertWidgetAt(shell: DashboardShell, widgets: LayoutWidget[], id: string, index: number): LayoutWidget[] {
  const def = widgetDefinition(shell, id);
  if (!def || widgets.some((w) => w.id === id)) return widgets;
  const at = Math.max(0, Math.min(widgets.length, index));
  const next = [...widgets];
  next.splice(at, 0, { id, order: at, size: def.defaultSize, config: {} });
  return renumber(next);
}

/** Columns of the edit grid: stores have three, vendors two (their widgets are half or full width). */
export const GRID_COLUMNS: Record<DashboardShell, number> = { store: 3, vendor: 2 };

/** Columns a size spans: narrow is one, wide two, full every column. */
export const sizeSpan = (shell: DashboardShell, size: WidgetSize): number =>
  size === 'full' ? GRID_COLUMNS[shell] : size === 'wide' ? 2 : 1;

/** The size a span of columns stands for, or null when the shell has no such size. */
export const spanToSize = (shell: DashboardShell, span: number): WidgetSize | null => {
  const cols = GRID_COLUMNS[shell];
  if (span >= cols) return 'full';
  if (span === 2) return 'wide';
  if (span === 1) return 'narrow';
  return null;
};

/** True when two widget lists differ in order, size or config. */
export const layoutsDiffer = (a: LayoutWidget[], b: LayoutWidget[]): boolean =>
  JSON.stringify(renumber(a)) !== JSON.stringify(renumber(b));

export function resizeWidget(shell: DashboardShell, widgets: LayoutWidget[], id: string, size: WidgetSize): LayoutWidget[] {
  const def = widgetDefinition(shell, id);
  if (!def || !def.allowedSizes.includes(size)) return widgets;
  return widgets.map((w) => (w.id === id ? { ...w, size } : w));
}

export function configureWidget(widgets: LayoutWidget[], id: string, config: WidgetConfig): LayoutWidget[] {
  return widgets.map((w) => (w.id === id ? { ...w, config } : w));
}

/** Weeks the sync chart shows for a `window` setting; unset keeps today's 12. */
export const windowToWeeks = (window: WidgetWindow | undefined): number =>
  window === '4w' ? 4 : window === '26w' ? 26 : 12;

export const toLayout = (widgets: LayoutWidget[]): DashboardLayout => ({ version: 1, widgets: renumber(widgets) });
